import { BrowserRouter as Router, NavLink, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import DashboardCards from "./components/DashboardCards";
import StatisticsPage from "./pages/StatisticsPage";
import LinksPage from "./pages/LinksPage";
import Top10Partners from "./pages/Top10Partners";
import WithdrawPage from "./pages/WithdrawPage";
import AffiliateRegisterPage from "./pages/AffiliateRegisterPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import useAffiliateAnalytics from "./hooks/useAffiliateAnalytics";

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-center" />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

function AppRoutes() {
  const { user, authLoading } = useAuth();
  const analytics = useAffiliateAnalytics(user);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[var(--aff-bg)] text-white flex items-center justify-center">
        <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-4 text-sm text-slate-300">
          Loading affiliate workspace...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--aff-bg)] text-white">
        <AffiliateRegisterPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--aff-bg)] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(28,215,174,0.16),transparent_32%),radial-gradient(circle_at_top_right,rgba(255,184,77,0.14),transparent_28%),linear-gradient(180deg,rgba(8,13,24,0),rgba(8,13,24,0.82))]" />
      <div className="relative flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 md:pl-[280px]">
          <Navbar user={user} analytics={analytics.data} loading={analytics.loading} />
          <div className="md:hidden overflow-x-auto border-b border-white/10 bg-[rgba(7,12,21,0.72)] px-4 py-3 backdrop-blur-xl">
            <div className="flex gap-2">
              {[
                ["/", "Dashboard"],
                ["/statistics", "Stats"],
                ["/links", "Links"],
                ["/withdraw", "Withdraw"],
              ].map(([to, label]) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `rounded-full px-4 py-2 text-sm font-semibold ${
                      isActive
                        ? "bg-emerald-400/15 text-white"
                        : "bg-white/[0.05] text-slate-300"
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
          <main className="flex-1 px-4 py-5 md:px-8 md:py-8">
            <Routes>
              <Route path="/register" element={<AffiliateRegisterPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardCards
                      user={user}
                      analytics={analytics.data}
                      loading={analytics.loading}
                      onRefresh={analytics.refresh}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistics"
                element={
                  <ProtectedRoute>
                    <StatisticsPage
                      user={user}
                      analytics={analytics.data}
                      loading={analytics.loading}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/links"
                element={
                  <ProtectedRoute>
                    <LinksPage
                      user={user}
                      analytics={analytics.data}
                      loading={analytics.loading}
                      onRefresh={analytics.refresh}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/top10-partners"
                element={
                  <ProtectedRoute>
                    <Top10Partners analytics={analytics.data} loading={analytics.loading} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/withdraw"
                element={
                  <ProtectedRoute>
                    <WithdrawPage
                      user={user}
                      analytics={analytics.data}
                      onRefresh={analytics.refresh}
                    />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
